import { useCallback, useEffect, useState } from 'react';
import communicationApi from '../../../../services/communicationApi';
import { MessageComposition } from '../../../../types/communication';

export interface CompositionOption {
    sendId: string;
    uuid: string;
    name: string;
    channel: string;
    active: boolean;
    groupName?: string | null;
}

const sendIdOf = (composition: MessageComposition): string | null => {
    if (composition.sendId) return composition.sendId;
    const source = composition.raw as unknown as Record<string, unknown>;
    const legacy = source?.legacyId ?? source?.legacy_id;
    return typeof legacy === 'string' && legacy ? legacy : null;
};

export const loadCollectionCompositions = async (): Promise<CompositionOption[]> => {
    const page = await communicationApi.listMessageCompositions({ pageSize: 500 });
    return page.list
        .filter(c => c.channel === 'EMAIL' || c.channel === 'EXTERNAL_MESSAGE')
        .map(c => ({ composition: c, sendId: sendIdOf(c) }))
        .filter((entry): entry is { composition: MessageComposition; sendId: string } => entry.sendId !== null)
        .map(({ composition, sendId }) => ({
            sendId,
            uuid: composition.id,
            name: composition.name,
            channel: composition.channel,
            active: composition.status === 'ACTIVE',
            groupName: composition.groupName ?? null,
        }));
};

const useCollectionCompositions = () => {
    const [options, setOptions] = useState<CompositionOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [version, setVersion] = useState(0);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setFailed(false);
        loadCollectionCompositions()
            .then(list => { if (active) setOptions(list); })
            .catch(() => { if (active) setFailed(true); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [version]);

    const reload = useCallback(() => setVersion(v => v + 1), []);

    return { options, loading, failed, reload };
};

export default useCollectionCompositions;
